import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const HERO_IMG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_lifeplan-schedule/artifacts/432azqxv_image0%20%283%29.webp";

const LINES = [
  "You worked for it.",
  "You earned every dollar.",
  "Now let's protect it.",
];

// Line-by-line masked reveal with staggered inner translate
const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.7 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen bg-ink grain overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax image layer */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        {/* Spotlight overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/60 to-ink" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_40%,transparent_0%,rgba(10,17,40,0.85)_100%)]" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 pt-40 md:pt-48 pb-24 min-h-screen flex flex-col justify-between"
      >
        <div>
          {/* Chapter label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9 }}
            className="text-gold text-[11px] tracking-[0.35em] mb-10 md:mb-16 flex items-center gap-4"
          >
            <span className="w-8 h-[1px] bg-gold" />
            CHAPTER 00 · NO SUITS · NO SLIDES · JUST STRAIGHT TALK
          </motion.div>

          {/* Kinetic masked headline */}
          <h1 className="font-serif text-cream text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] leading-[0.92] tracking-tight font-light max-w-[1300px]" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  className="mask-inner"
                  custom={i}
                  initial="hidden"
                  animate="show"
                  variants={lineVariants}
                >
                  {i === 2 ? (
                    <>
                      Now let's <span className="italic text-gold font-light">protect it</span>.
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>

        {/* Bottom row */}
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-10 mt-24 md:mt-32">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="text-cream/70 text-base md:text-lg max-w-md leading-relaxed font-light"
          >
            Independent Midwest brokerage for people who work with their
            hands, run their own show, and want their family covered the
            right way — the first time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 1 }}
            className="flex flex-col gap-6"
          >
            <a
              href="#schedule"
              onClick={(e) => { e.preventDefault(); document.querySelector('#schedule')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group inline-flex items-center gap-6 text-cream border-b border-cream/40 pb-3 hover:border-gold hover:text-gold transition-colors duration-500"
              data-testid="hero-schedule-cta"
            >
              <span className="text-[12px] tracking-[0.3em] uppercase">Schedule a private consultation</span>
              <svg width="40" height="16" viewBox="0 0 40 16" fill="none" className="transition-transform duration-500 group-hover:translate-x-2">
                <path d="M0 8h38m0 0L30 1m8 7l-8 7" stroke="currentColor" strokeWidth="1" />
              </svg>
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-cream/50 text-[10px] tracking-[0.3em] flex flex-col items-center gap-3"
      >
        <span>SCROLL</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-[1px] h-8 bg-cream/40"
        />
      </motion.div>
    </section>
  );
}
